import React from "react";
import { FeatherCheck, FeatherAlertCircle } from "@subframe/core";
import { TextField } from "../../../../../ui/components/TextField";
import { FieldFootnote } from "./FieldFootnote";
import { FieldRowProps } from "./fieldTypes";

/**
 * A credential the server stores but never sends back.
 *
 * The field is always empty on load — not filled with bullets. A masked
 * placeholder is indistinguishable from a real value once it is in the input,
 * so the user cannot tell whether they are about to replace a secret or type
 * one for the first time, and posting the form back would save the mask over
 * the real credential.
 *
 * Instead the state is stated above the box in words, from the server's
 * `secret_set` flag, and the box asks for a replacement. Leaving it blank keeps
 * whatever is stored — enforced on the server, not just here.
 */
export const SecretFieldRow: React.FC<FieldRowProps> = ({
  config,
  value,
  locked,
  lockReason,
  errorMessage,
  statusNote,
  onChange,
}) => {
  const configured = statusNote === "set";

  return (
    <div className="flex w-full flex-col gap-1">
      {statusNote !== null && statusNote !== undefined && (
        <span
          className={`flex items-center gap-1 text-caption font-caption ${
            configured ? "text-success-700" : "text-subtext-color"
          }`}
        >
          {configured ? (
            <>
              <FeatherCheck className="shrink-0 text-caption font-caption" />
              Configured. Leave blank to keep the current one.
            </>
          ) : (
            <>
              <FeatherAlertCircle className="shrink-0 text-caption font-caption" />
              Not configured. GSTIN verification will fail until one is saved.
            </>
          )}
        </span>
      )}

      <TextField
        className="w-full"
        label={config.label}
        disabled={locked}
        error={Boolean(errorMessage)}
      >
        <TextField.Input
          type="password"
          autoComplete="new-password"
          value={value}
          placeholder={configured ? "Enter a new secret to replace it" : config.placeholder}
          disabled={locked}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        />
      </TextField>

      <FieldFootnote
        hint={config.hint}
        example={config.example}
        lockReason={lockReason}
        errorMessage={errorMessage}
      />
    </div>
  );
};
